/**
 * kb-qa-tab.tsx — Tab "Hỏi đáp" của một kho.
 * Trái: cây danh mục HỎI ĐÁP của kho (cây riêng, không dùng chung với Tài liệu / Ảnh và video) — thêm / sửa / xoá / kéo sắp xếp.
 * Phải: tìm câu hỏi / câu trả lời, lọc trạng thái, "Tìm kiếm", "Xuất hỏi đáp", "Nhập từ mẫu" (CSV), "+ Thêm nội dung";
 * bảng: chọn nhiều (xoá / bật / tắt / chuyển danh mục hàng loạt), ID, câu hỏi (+n câu tương tự), danh mục, câu trả lời,
 * thời gian cập nhật, trạng thái (công tắc), Chỉnh sửa / Xoá.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download, FileUp, Loader2, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch, Checkbox } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  useKnowledgeEntries, useKnowledgeCategories, useDeleteKnowledgeEntry, useBulkKnowledge, type KnowledgeEntry,
} from '@/hooks/use-knowledge'
import { setKnowledgeEnabled, importQaCsv, exportQaCsv } from '@/hooks/use-ai-train-test'
import { QA_CSV_TEMPLATE, downloadText } from '../train/train-lib'
import { KbCategoryTree } from './kb-category-tree'
import { KbCategorySelect } from './kb-category-select'
import { KbQaDialog } from './kb-qa-dialog'
import { descendantIds, shortId, type KnowledgePerms } from './kb-lib'

type StatusFilter = 'all' | 'on' | 'off' | 'pending'
const STATUS_LABELS: Record<StatusFilter, string> = {
  get all() { return i18n.t('aiKnow:qa.status.all') },
  get on() { return i18n.t('aiKnow:qa.status.on') },
  get off() { return i18n.t('aiKnow:qa.status.off') },
  get pending() { return i18n.t('aiKnow:qa.status.pending') },
}

const fmtTime = (s: string) => new Date(s).toLocaleString(currentLocale(), { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })

export function KbQaTab({ baseId, perms, categoryId, onCategory, openAdd, onOpenAddHandled }: {
  baseId: string
  perms: KnowledgePerms
  categoryId: string
  onCategory: (id: string) => void
  /** true → mở hộp "Thêm hỏi đáp" một lần (từ màn chào kho trống), rồi gọi onOpenAddHandled. */
  openAdd?: boolean
  onOpenAddHandled?: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const { data: cats } = useKnowledgeCategories(baseId, { kind: 'qa' })
  const { data: entries, isLoading, isError } = useKnowledgeEntries({ knowledgeBaseId: baseId, format: 'qa', preview: '1' }, { pollWhileBusy: true })
  const del = useDeleteKnowledgeEntry()
  const bulk = useBulkKnowledge()
  const { canCreate, canUpdate, canDelete } = perms
  /** Cột chọn nhiều + cột Thao tác chỉ hiện khi có knowledge.update hoặc knowledge.delete. */
  const canManageRows = canUpdate || canDelete
  const [draft, setDraft] = useState('')
  const [draftStatus, setDraftStatus] = useState<StatusFilter>('all')
  const [applied, setApplied] = useState<{ q: string; status: StatusFilter }>({ q: '', status: 'all' })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<KnowledgeEntry | null>(null)
  const [adding, setAdding] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [moveTo, setMoveTo] = useState('')
  useEffect(() => {
    if (!openAdd) return
    setAdding(true)
    onOpenAddHandled?.()
  }, [openAdd, onOpenAddHandled])

  const scope = useMemo(() => (categoryId ? new Set(descendantIds(cats ?? [], categoryId)) : null), [cats, categoryId])
  const rows = useMemo(() => {
    const q = applied.q.trim().toLowerCase()
    return (entries ?? []).filter((e) => {
      if (scope && !(e.categoryId && scope.has(e.categoryId))) return false
      if (applied.status === 'on' && (e.status === 'archived' || e.status === 'pending')) return false
      if (applied.status === 'off' && e.status !== 'archived') return false
      if (applied.status === 'pending' && e.status !== 'pending') return false
      if (!q) return true
      return (e.title ?? '').toLowerCase().includes(q) || e.content.toLowerCase().includes(q)
        || (e.questions ?? []).some((x) => x.toLowerCase().includes(q))
    })
  }, [entries, scope, applied])
  const catName = (id: string | null) => (cats ?? []).find((c) => c.id === id)?.name
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const picked = rows.filter((r) => selected.has(r.id)).map((r) => r.id)

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)))
  }
  function toggleOne(id: string) {
    setSelected((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x })
  }

  async function toggle(e: KnowledgeEntry, on: boolean) {
    try {
      await setKnowledgeEnabled(e.id, on)
      await qc.invalidateQueries({ queryKey: ['knowledge'] })
    } catch (err) { toast.error(apiError(err)) }
  }

  async function remove(e: KnowledgeEntry) {
    if (!window.confirm(t('qa.confirmDelete', { title: e.title ?? '' }))) return
    try { await del.mutateAsync(e.id); toast.success(t('qa.deleted')) } catch (err) { toast.error(apiError(err)) }
  }

  async function runBulk(action: 'delete' | 'enable' | 'disable' | 'move') {
    if (!picked.length) return
    if (action === 'delete' && !window.confirm(t('qa.confirmDeleteMany', { n: picked.length }))) return
    try {
      const r = await bulk.mutateAsync({ ids: picked, action, ...(action === 'move' ? { categoryId: moveTo || null } : {}) })
      toast.success(t(`qa.bulkDone.${action}`, { n: r.done }))
      setSelected(new Set())
    } catch (err) { toast.error(apiError(err)) }
  }

  async function exportAll() {
    try { downloadText(await exportQaCsv(categoryId || null, baseId), 'hoi-dap.csv') } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
      <KbCategoryTree baseId={baseId} kind="qa" selected={categoryId} onSelect={(id) => { onCategory(id); setSelected(new Set()) }} perms={perms} totalCount={(entries ?? []).length} />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <form className="flex min-w-0 flex-1 flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); setApplied({ q: draft, status: draftStatus }) }}>
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8" placeholder={t('qa.searchPlaceholder')} value={draft} onChange={(e) => setDraft(e.target.value)} />
            </div>
            <Select value={draftStatus} onValueChange={(v) => setDraftStatus(v as StatusFilter)}>
              <SelectTrigger className="h-8 w-40" aria-label={t('qa.statusLabel')}><SelectValue placeholder={t('qa.statusLabel')} /></SelectTrigger>
              <SelectContent>{(Object.keys(STATUS_LABELS) as StatusFilter[]).map((k) => <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="submit" variant="secondary">{t('common:actions.search')}</Button>
            <Button type="button" variant="ghost" onClick={() => void exportAll()}><Download className="h-4 w-4" /> {t('qa.export')}</Button>
          </form>
          {canCreate && <Button variant="outline" className="border-primary/40 text-primary" onClick={() => setImportOpen(true)}><FileUp className="h-4 w-4" /> {t('qa.importFromTemplate')}</Button>}
          {canCreate && <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> {t('qa.addContent')}</Button>}
        </div>

        {picked.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
            <span className="font-medium">{t('qa.selected', { n: picked.length })}</span>
            {canUpdate && (
              <>
                <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('enable')}>{t('common:actions.enable')}</Button>
                <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('disable')}>{t('common:actions.disable')}</Button>
                <span className="flex items-center gap-1">
                  <KbCategorySelect knowledgeBaseId={baseId} kind="qa" value={moveTo} onChange={setMoveTo} className="h-7 w-48 text-xs" noneLabel={t('qa.noCategory')} />
                  <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('move')}>{t('qa.moveCategory')}</Button>
                </span>
              </>
            )}
            {canDelete && <Button size="sm" variant="ghost" className="text-destructive" disabled={bulk.isPending} onClick={() => void runBulk('delete')}>{t('common:actions.delete')}</Button>}
            {bulk.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <button type="button" className="ml-auto text-xs text-muted-foreground hover:underline" onClick={() => setSelected(new Set())}>{t('qa.deselect')}</button>
          </div>
        )}

        {isLoading && !entries ? (
          <Loading label={t('qa.loading')} />
        ) : isError ? (
          <ErrorState message={t('qa.loadError')} />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            {applied.q || applied.status !== 'all' ? t('qa.emptyFiltered') : categoryId ? t('qa.emptyCategory') : canCreate ? t('qa.emptyCanCreate') : t('qa.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  {canManageRows && <th className="w-9 px-3 py-2"><Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label={t('common:actions.selectAll')} /></th>}
                  <th className="px-3 py-2 font-medium">ID</th>
                  <th className="px-3 py-2 font-medium">{t('qa.col.question')}</th>
                  <th className="px-3 py-2 font-medium">{t('qa.col.category')}</th>
                  <th className="px-3 py-2 font-medium">{t('qa.col.answer')}</th>
                  <th className="px-3 py-2 font-medium">{t('qa.col.updatedAt')}</th>
                  <th className="px-3 py-2 font-medium">{t('qa.col.status')}</th>
                  {canManageRows && <th className="px-3 py-2 text-right font-medium">{t('qa.col.actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((e) => (
                  <tr key={e.id} className={cn('align-top hover:bg-muted/30', selected.has(e.id) && 'bg-primary/5')}>
                    {canManageRows && <td className="px-3 py-2"><Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleOne(e.id)} aria-label={t('qa.pickRow')} /></td>}
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground" title={e.id}>{shortId(e.id)}</td>
                    <td className="max-w-[260px] px-3 py-2">
                      <p className="line-clamp-2 font-medium" title={e.title ?? ''}>{e.title}</p>
                      {!!e.questions?.length && (
                        <Badge variant="secondary" className="mt-0.5 text-[10px]" title={e.questions.join('\n')}>{t('qa.similarCount', { n: e.questions.length })}</Badge>
                      )}
                      {e.status === 'pending' && <Badge variant="warning" className="ml-1 mt-0.5 text-[10px]">{t('qa.status.pending')}</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs">{catName(e.categoryId) ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="max-w-[320px] px-3 py-2"><p className="line-clamp-2 text-xs text-muted-foreground">{e.content}</p></td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{fmtTime(e.updatedAt)}</td>
                    <td className="px-3 py-2">
                      {canUpdate
                        ? <Switch checked={e.status !== 'archived'} onCheckedChange={(on) => void toggle(e, on)} aria-label={t('qa.toggle')} />
                        : <Badge variant={e.status === 'archived' ? 'secondary' : 'success'} className="text-[10px]">{e.status === 'archived' ? STATUS_LABELS.off : STATUS_LABELS.on}</Badge>}
                    </td>
                    {canManageRows && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-xs">
                        {canUpdate && <button type="button" className="font-medium text-primary hover:underline" onClick={() => setEditing(e)}>{t('qa.editRow')}</button>}
                        {canDelete && <button type="button" className="ml-3 font-medium text-destructive hover:underline" onClick={() => void remove(e)}>{t('common:actions.delete')}</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <KbQaDialog baseId={baseId} defaultCategoryId={categoryId} onClose={() => setAdding(false)} />}
      {editing && <KbQaDialog baseId={baseId} entry={editing} onClose={() => setEditing(null)} />}
      {importOpen && <QaImportDialog baseId={baseId} categoryId={categoryId} onClose={() => setImportOpen(false)} />}
    </div>
  )
}

/** "Nhập từ mẫu": tải mẫu CSV → điền → chọn tệp. Câu hỏi trùng trong kho được cập nhật, không tạo bản sao. */
function QaImportDialog({ baseId, categoryId, onClose }: { baseId: string; categoryId: string; onClose: () => void }) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [cat, setCat] = useState(categoryId)

  async function onFile(file: File) {
    setBusy(true)
    try {
      const r = await importQaCsv(await file.text(), cat || null, baseId)
      const extra = r.errors.length ? t('qa.import.errorLines', { n: r.errors.length, lines: r.errors.slice(0, 5).map((e) => e.line).join(', ') }) : ''
      const cats = r.unknownCategories.length ? t('qa.import.unknownCategories', { names: r.unknownCategories.join(', ') }) : ''
      toast.success(t('qa.import.done', { created: r.created, updated: r.updated, extra, cats }))
      await qc.invalidateQueries({ queryKey: ['knowledge'] })
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('qa.import.title')}</DialogTitle>
          <DialogDescription>{t('qa.import.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Button variant="outline" size="sm" onClick={() => downloadText(QA_CSV_TEMPLATE, 'mau-hoi-dap.csv')}><Download className="h-4 w-4" /> {t('qa.import.downloadTemplate')}</Button>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('qa.import.defaultCategory')}</p>
            <KbCategorySelect knowledgeBaseId={baseId} kind="qa" value={cat} onChange={setCat} />
          </div>
          <p className="text-[11px] text-muted-foreground">{t('qa.import.dupNote')}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {t('qa.import.chooseCsv')}
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = '' }} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
